import { useEffect, useRef, useState } from "react";
import type { HandoffDeliveryResult } from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Button, Control } from "./components";

export function HandoffDelivery({ id, label }: { id: string; label: string }) {
  const transport = useWorkspaceTransport();
  const [targetId, setTarget] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState<{
      key: string;
      transport: typeof transport;
      result: HandoffDeliveryResult;
    }>();
  const key = JSON.stringify([id, targetId.trim()]),
    current = useRef({ key, transport });
  current.current = { key, transport };
  const controller = useRef<AbortController | undefined>(undefined);
  useEffect(() => {
    setBusy(false);
    setError("");
    return () => controller.current?.abort();
  }, [transport, id]);
  const result =
    saved?.key === key && saved.transport === transport
      ? saved.result
      : undefined;
  return (
    <section className="handoff-delivery" aria-label="Submit approved package">
      <h3>Send to {label}</h3>
      <p>Enter the cart or order reference supplied by your ordering system.</p>
      <Control label="Cart or order reference">
        <input
          value={targetId}
          maxLength={200}
          disabled={busy}
          onChange={(event) => {
            setTarget(event.target.value);
            setError("");
          }}
        />
      </Control>
      <Button
        disabled={busy || !targetId.trim() || !!result}
        onClick={() => {
          const abort = new AbortController();
          controller.current?.abort();
          controller.current = abort;
          setBusy(true);
          setError("");
          void transport.client
            .deliverHandoff(id, { targetId: targetId.trim() }, abort.signal)
            .then((result) => {
              if (
                !abort.signal.aborted &&
                current.current.key === key &&
                current.current.transport === transport
              )
                setSaved({ key, transport, result });
            })
            .catch((failure: Error) => {
              if (
                !abort.signal.aborted &&
                current.current.key === key &&
                current.current.transport === transport
              )
                setError(failure.message);
            })
            .finally(() => {
              if (!abort.signal.aborted) setBusy(false);
            });
        }}
      >
        {busy
          ? "Sending approved package…"
          : result
            ? "Order submission confirmed"
            : "Submit approved package"}
      </Button>
      {error && (
        <p role="alert">
          {error}. After an uncertain response, retry this same package and
          reference to recover its receipt.
        </p>
      )}
      {result && (
        <p role="status">
          {result.replayed
            ? "Existing submission recovered."
            : "Submission confirmed."}{" "}
          Order {result.receipt.orderId}, line {result.receipt.lineId}.
          Committed {new Date(result.receipt.committedAt).toLocaleString()}.
        </p>
      )}
    </section>
  );
}
